import { Outlet } from 'react-router'
import { toast } from 'sonner'
import type { ServiceCursorPaginatedResponseType } from '@/connect-types/backend/service'
import morpheusApi from '@/state/morpheus/morpheus.slice'
import type { VenueBrandingSettingsType } from '@/state/morpheus/types/venue'
import { buildUrl } from '@/utils/common'

const morpheusApiwithTags = morpheusApi.enhanceEndpoints({
  addTagTypes: ['Brand', 'Pallet'],
})

/**
 * One page big enough to hold an organisation's entire brand-kit list.
 *
 * `getBrands` is cursor-paginated with NO search parameter, and morpheus orders by
 * `created_at desc` (`organisations/:orgId/brand/controller.ts:754`). Anything that
 * fetches a single short page therefore gets the N most RECENTLY CREATED kits, not the
 * first N by name, and `transformResponse` below then sorts that page alphabetically,
 * which disguises the truncation completely: the result looks like a complete A-Z list.
 *
 * That is how a 100-limit on the list page hid kits the venue-level picker (2000) could
 * see. Searching "fo" returned The Bedford and The Four Horseshoes while FOLEY's,
 * Foxtail NYC and Flat Earth Pizza were absent, because the search filters the fetched
 * page client-side and those kits were never fetched.
 *
 * Matches the limit `common/brand/index.tsx` already uses, so the two agree. This is a
 * ceiling, not a fix: an organisation past it silently truncates again, and the real
 * answer is a search parameter on the endpoint.
 */
export const BRAND_KIT_PAGE_LIMIT = 2000

export const brand = morpheusApiwithTags.injectEndpoints({
  endpoints: (build) => ({
    getBrandPallet: build.query<
      Record<number | 'DEFAULT', string>,
      { orgId: string; code: string }
    >({
      query: ({ orgId, code }) => ({
        method: 'GET',
        url: `/organisations/${orgId}/brand/string-to-pallet`,
        params: { code },
      }),
      providesTags: (item, _, { code }) => [{ type: 'Pallet', id: code }],
    }),
    getPreview: build.query<
      string,
      {
        orgId: string
        id: string
        serial: string
        context?: string
        type?: 'validation' | 'reviews' | 'review-reply'
        metadata?: string
      }
    >({
      query: ({
        orgId,
        id,
        serial,
        type = 'validation',
        context,
        metadata,
      }) => ({
        method: 'GET',
        url: buildUrl(`/organisations/${orgId}/brand/${id}/preview`, {
          serial,
          type,
          context_type: context,
          metadata,
        }),
      }),
    }),
    getBrands: build.query<
      ServiceCursorPaginatedResponseType<VenueBrandingSettingsType>,
      { orgId: string; cursor: string; limit: number }
    >({
      query: ({ orgId, ...rest }) => ({
        method: 'GET',
        url: buildUrl(`/organisations/${orgId}/brand`, rest),
      }),
      providesTags: (items: any) => [
        { type: 'Brand', id: 'LIST' },
        ...(items?.data?.map((item: any) => ({
          type: 'Brand' as const,
          id: item?.id,
        })) ?? []),
      ],
      transformResponse(
        data: ServiceCursorPaginatedResponseType<VenueBrandingSettingsType>
      ) {
        const sortedData = {
          ...data,
          data: [...data.data].sort((a, b) => a.name.localeCompare(b.name)),
        }
        return sortedData
      },
    }),
    getBrand: build.query<
      VenueBrandingSettingsType,
      { orgId: string; id: string }
    >({
      query: ({ orgId, id }) => ({
        method: 'GET',
        url: `/organisations/${orgId}/brand/${id}`,
      }),
      providesTags: (item: any) => [{ type: 'Brand', id: item?.id }],
    }),
    updateBrand: build.mutation<
      VenueBrandingSettingsType,
      { orgId: string; data: VenueBrandingSettingsType }
    >({
      query: ({ orgId, data }) => ({
        method: 'PUT',
        url: `/organisations/${orgId}/brand/${data.id}`,
        data,
      }),
      invalidatesTags: (item: any) => [
        { type: 'Brand', id: item?.id },
        { type: 'Brand', id: 'LIST' },
      ],
      transformResponse(data: VenueBrandingSettingsType) {
        toast.success(`Brand kit (${data.name}) updated`)
        return data
      },
    }),
    createBrand: build.mutation<
      VenueBrandingSettingsType,
      { orgId: string; data: Partial<VenueBrandingSettingsType> }
    >({
      query: ({ orgId, data }) => ({
        method: 'POST',
        url: `/organisations/${orgId}/brand`,
        data,
      }),
      invalidatesTags: [{ type: 'Brand', id: 'LIST' }],
    }),
    deleteBrand: build.mutation<
      VenueBrandingSettingsType,
      { orgId: string; id: string }
    >({
      query: ({ orgId, id }) => ({
        method: 'DELETE',
        url: `/organisations/${orgId}/brand/${id}`,
      }),
      invalidatesTags: (item: any) => [
        { type: 'Brand', id: item?.id },
        { type: 'Brand', id: 'LIST' },
      ],
    }),
    brandSocketUpdate: build.mutation<
      VenueBrandingSettingsType,
      { orgId: string; data: VenueBrandingSettingsType }
    >({
      query: ({ orgId, data }) => ({
        method: 'PUT',
        url: `/organisations/${orgId}/brand/${data.id}/socket`,
        data,
      }),
    }),
  }),
})

function Layout() {
  return <Outlet />
}

export default Layout
